import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { mapListing, mapOrder } from '../lib/chatMappers';

/**
 * Everything the details panel shows about the counterparty.
 *
 * All three are real endpoints keyed by the other party's user id:
 *   orders   GET /api/orders/chat-context?counterpartyId=   (bare array, snake_case)
 *   products GET /api/listings?sellerId=
 *   profile  GET /api/users/public/:username                (optional, may 404)
 *
 * The two panels are independent so a slow listing query never blocks the
 * order history, and each reports its own empty/loading state.
 */
export function useSellerPanel({ partnerId, username, enabled, includeListings = true }) {
  const [orders, setOrders] = useState([]);
  const [ordersStatus, setOrdersStatus] = useState('idle');
  const [products, setProducts] = useState([]);
  const [productsStatus, setProductsStatus] = useState('idle');
  const [profile, setProfile] = useState(null);

  const load = useCallback(async () => {
    if (!enabled || !partnerId) {
      setOrders([]);
      setProducts([]);
      setProfile(null);
      setOrdersStatus('idle');
      setProductsStatus('idle');
      return;
    }

    setOrdersStatus('loading');
    setProductsStatus('loading');

    // Orders between the two of us, in either direction.
    api
      .get(`/api/orders/chat-context?counterpartyId=${encodeURIComponent(partnerId)}`)
      .then((rows) => {
        setOrders((Array.isArray(rows) ? rows : []).map(mapOrder));
        setOrdersStatus('ready');
      })
      .catch(() => {
        setOrders([]);
        setOrdersStatus('error');
      });

    // The counterparty's live store listings. Skipped entirely when the viewer is
    // a seller: the counterparty is then a buyer, who has no listings to show.
    if (includeListings) {
      setProductsStatus('loading');
      api
        .get(`/api/listings?sellerId=${encodeURIComponent(partnerId)}&limit=4`)
        .then((data) => {
          setProducts((data?.items ?? []).map(mapListing));
          setProductsStatus('ready');
        })
        .catch(() => {
          setProducts([]);
          setProductsStatus('error');
        });
    } else {
      setProducts([]);
      setProductsStatus('ready');
    }

    // Public profile is best-effort: seeded accounts have no username and the
    // endpoint 404s, which is not an error worth surfacing.
    if (username) {
      api
        .get(`/api/users/public/${encodeURIComponent(username)}`)
        .then((data) => setProfile(data?.user ?? data ?? null))
        .catch(() => setProfile(null));
    } else {
      setProfile(null);
    }
  }, [enabled, partnerId, username]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    orders,
    ordersStatus,
    products,
    productsStatus,
    profile,
    reload: load,
  };
}
